import { useEffect, useMemo, useState, useCallback } from 'react'
import { sortBy } from 'lodash'
import dayjs from 'dayjs'
import { parseUtc, formatDateTime } from '../../utils/dateHelpers'
import useTimeFormat from '../../hooks/useTimeFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  LBAlert,
  LBAlertDescription,
  LBTableRow,
  LBTableCell,
  LBButton,
  LBBadge,
  LBSwitch,
  LBExportFileIcon,
  LBCopyIcon,
  LBChevronDownIcon,
  LBDropdownMenu,
  LBDropdownMenuTrigger,
  LBDropdownMenuContent,
  LBDropdownMenuItem,
  LBDropdownMenuSeparator,
  LBSelect,
  LBSelectTrigger,
  LBSelectValue,
  LBSelectContent,
  LBSelectItem,
  LBStarIcon,
  LBAiInsightsIcon,
  LBBotIcon,
  LBLayoutIcon,
  LBShowIcon,
  LBSkeleton,
  LBTooltip,
  LBTooltipTrigger,
  LBTooltipContent,
  LBFilterOffIcon,
  lbToast
} from '@linearb/buzz'

dayjs.extend(relativeTime)
import {
  showSupportAccountSuccessToast,
  showSupportAccountErrorToast
} from '../../utils/toastHelpers'
import {
  convertToCsv,
  convertToJsonArray,
  downloadFile,
  getFilenameTimestamp
} from '../../utils/csvExport'
import { BuyerChip, buyerEngagedLabel } from '../../utils/buyerDisplay'
import { isSupportEmail } from '../../utils/supportAccount'
import { orgTierPreferenceStorage } from '../../utils/orgTierPreferenceStorage'
import FilteredTable from '../../custom-components/FilteredTable'
import FeatureFlagFilter from '../../custom-components/FeatureFlagFilter'
import FeatureFlagOrgToggle from '../../custom-components/FeatureFlagOrgToggle'
import SensorsIcon, {
  ICONS_CONFIG,
  SensorName,
  CLAUDE_METHOD_LABEL,
  CODEX_METHOD_LABEL,
  WINDSURF_METHOD_LABEL,
  COPILOT_AUTH_TYPE_LABEL
} from '../../custom-components/SensorsIcons'
import CopyButton from '../../custom-components/CopyButton'
import FavoriteOrgButton from '../../custom-components/FavoriteOrgButton'
import SfdcAccountName from '../../custom-components/SfdcAccountName'
import { HAS_US_DATA_SOURCES } from '../../constants/apis'
import CompanyLogo from '../../custom-components/CompanyLogo'
import {
  LicenseTypeBadge,
  LicensePlanBadge
} from '../../custom-components/LicenseBadges'
import SettingChangeConfirmationDialog from '../OrganizationDetails/SettingChangeConfirmationDialog'
import OrgInsightsDrawer from '../OrganizationDetails/OrgInsightsDrawer'
import HealthScoreBadge from '../OrganizationDetails/HealthScoreBadge'
import FeatureFlagRoleIndicator from '../../custom-components/FeatureFlagRoleIndicator'
import { useNavigate } from 'react-router-dom'
import { useFilteredTableQuery } from '../../hooks/useFilteredTableQuery'
import type { PaginatedData } from '../../hooks/useFilteredTableQuery'
import { queryKeys } from '../../lib/queryKeys'
import { trackAction } from '../../lib/datadogRum'
import { usd } from '../../lib/numberFormat'
import type { Organization } from '../../types'
import {
  getOrganizations,
  getOrganizationsByIds
} from '../../apis/organizations'
import {
  useSetSupportAccount,
  useOrganizationHasInsights,
  useOrganizationAiChatCount,
  useOrganizationCustomDashboards
} from '../../hooks/queries/useOrganizations'
import { useMultiOrgGroups } from '../../hooks/queries/useMultiOrg'
import { useQueryClient } from '@tanstack/react-query'
import useUserInfo from '../../hooks/useUserInfo'
import useFavoriteOrgs from '../../hooks/useFavoriteOrgs'
import useInsightsPreferences from '../../hooks/useInsightsPreferences'
import { BILLABLE_USERS_TOOLTIP } from '../../constants/tooltips'
import { getOrgToggleBlockedReason } from '../../constants/featureFlagStatus'

const InsightsBadge = ({
  orgId,
  orgName,
  onOpen
}: {
  orgId: string | number
  orgName: string
  onOpen: (target: { id: string | number; name: string }) => void
}) => {
  const { data } = useOrganizationHasInsights(orgId)
  const hasInsights = !!data?.has_insights
  const buyerCount = data?.buyer_count ?? 0
  const hasBuyer = buyerCount > 0
  if (!hasInsights && !hasBuyer) {
    return null
  }
  const button = (
    <LBButton
      size='sm'
      onClick={e => {
        e.stopPropagation()
        trackAction('insights_opened', {
          orgId,
          orgName,
          insightsCount: data?.count ?? 0,
          buyerCount
        })
        onOpen({ id: orgId, name: orgName })
      }}
      className={`gap-2 cursor-pointer text-white ${
        hasInsights
          ? 'bg-indigo-500 hover:bg-indigo-600'
          : 'bg-emerald-500 hover:bg-emerald-600'
      }`}
    >
      {hasInsights && (
        <>
          <LBAiInsightsIcon className='h-4 w-4' />
          {data.count}
        </>
      )}
      <BuyerChip count={buyerCount} withSeparator={hasInsights} />
    </LBButton>
  )
  if (!hasBuyer) {
    return button
  }
  return (
    <LBTooltip>
      <LBTooltipTrigger asChild>{button}</LBTooltipTrigger>
      <LBTooltipContent>{buyerEngagedLabel(buyerCount)}</LBTooltipContent>
    </LBTooltip>
  )
}

const AiChatsBadge = ({ orgId }: { orgId: string | number }) => {
  const navigate = useNavigate()
  const { data } = useOrganizationAiChatCount(orgId)
  if (!data || data.session_count_7d === 0) {
    return <span className='text-muted-foreground'>—</span>
  }
  return (
    <LBTooltip>
      <LBTooltipTrigger asChild>
        <LBButton
          size='sm'
          onClick={e => {
            e.stopPropagation()
            navigate(`/organization/${orgId}#ai-chat-history`)
          }}
          className='gap-2 cursor-pointer text-white bg-sky-500 hover:bg-sky-600'
        >
          <LBBotIcon className='h-4 w-4' />
          {data.session_count_7d}
        </LBButton>
      </LBTooltipTrigger>
      <LBTooltipContent>
        <div className='space-y-1'>
          <div className='flex items-baseline justify-between gap-4'>
            <span className='opacity-70'>Last 7 days</span>
            <span className='font-medium'>
              {`${data.session_count_7d} chats · ${data.user_count_7d} users`}
            </span>
          </div>
          <div className='flex items-baseline justify-between gap-4'>
            <span className='opacity-70'>All time</span>
            <span className='font-medium'>
              {`${data.session_count} chats · ${data.user_count} users`}
            </span>
          </div>
        </div>
      </LBTooltipContent>
    </LBTooltip>
  )
}

const CustomDashboardsBadge = ({ orgId }: { orgId: string | number }) => {
  const navigate = useNavigate()
  const { data } = useOrganizationCustomDashboards(orgId)
  // Include support-account dashboards in the count; they're tagged in the
  // tooltip so the badge is never a misleading em dash when only support built.
  const sortedDashboards = useMemo(
    () => sortBy(data ?? [], d => d.name?.toLowerCase()),
    [data]
  )
  if (sortedDashboards.length === 0) {
    return <span className='text-muted-foreground'>—</span>
  }
  return (
    <LBTooltip>
      <LBTooltipTrigger asChild>
        <LBButton
          size='sm'
          onClick={e => {
            e.stopPropagation()
            navigate(`/organization/${orgId}#custom-dashboards`)
          }}
          className='gap-2 cursor-pointer text-white bg-violet-500 hover:bg-violet-600'
        >
          <LBLayoutIcon className='h-4 w-4' />
          {sortedDashboards.length}
        </LBButton>
      </LBTooltipTrigger>
      <LBTooltipContent>
        <div className='space-y-1 max-w-xs'>
          {sortedDashboards.map(dashboard => (
            <div
              key={dashboard.id}
              className='flex items-center justify-between gap-3'
            >
              <span className='truncate'>{dashboard.name}</span>
              <span className='flex items-center gap-1 shrink-0'>
                {isSupportEmail(dashboard.account_email) && (
                  <span className='text-xs opacity-60'>support</span>
                )}
                {dashboard.is_public && (
                  <LBShowIcon className='h-3 w-3 opacity-60' />
                )}
              </span>
            </div>
          ))}
        </div>
      </LBTooltipContent>
    </LBTooltip>
  )
}

export const ORGANIZATION_COLUMNS = [
  { title: '', width: '3%' },
  { title: 'Organization Name', width: '13%', sortKey: 'name' },
  { title: 'Organization ID', width: '9%', sortKey: 'id' },
  { title: 'License Type', width: '8%', sortKey: 'license_tier_type' },
  { title: 'License Plan', width: '9%', sortKey: 'license_plan_name' },
  { title: 'Active', width: '5%', sortKey: 'is_active' },
  {
    title: 'Billable Users',
    width: '8%',
    sortKey: 'billable_users',
    tooltip: BILLABLE_USERS_TOOLTIP
  },
  { title: 'ARR', width: '7%', sortKey: 'ARR' },
  { title: 'Latest Login', width: '10%', sortKey: 'latest_login' },
  { title: 'Integrations', width: '15%' },
  { title: 'Actions', width: '6%' },
  { title: 'Insights', width: '6%' },
  {
    title: 'AI Chats',
    width: '6%',
    tooltip: 'AI chats in the last 7 days'
  },
  {
    title: 'Dashboards',
    width: '6%',
    tooltip: 'Custom Metrics Builder dashboards built by this org'
  },
  {
    title: 'Health',
    width: '8%',
    sortKey: 'health_score',
    tooltip: 'Customer health score, scored per Salesforce family'
  }
]

// Trial End column is only shown when filtering by TRIAL tier, since
// trial_end_date is NULL for paid/free orgs (would just show em-dashes).
const TRIAL_END_COLUMN = {
  title: 'Trial End',
  width: '8%',
  sortKey: 'trial_end_date'
}

const toggleStates = {
  ALL: 'All',
  PAID: 'Paid',
  TRIAL: 'Trial',
  FREE: 'Free'
}

const activeToggleStates = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive'
}

const ALL_FILTER = 'all'

const PLAN_OPTIONS = [
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'business', label: 'Business' },
  { value: 'essentials', label: 'Essentials' },
  { value: 'pro', label: 'Pro' },
  { value: 'free 2.0', label: 'Free 2.0' },
  { value: 'free', label: 'Free' }
]

const DEPLOYMENT_OPTIONS = [
  { value: 'opa', label: 'OPA' },
  { value: 'cloud', label: 'Cloud' }
]

// Filterable AI method sub-options (value = `sensor:method`); badge labels come
// from the shared method maps. Enterprise is a Copilot connection type stored in
// a setting, not in profile.auth_type. github_app is omitted (not filterable here).
const AI_METHOD_FILTERS: {
  sensor: SensorName
  method: string
  disabled?: boolean
}[] = [
  { sensor: 'claude', method: 'admin_api' },
  { sensor: 'claude', method: 'analytics_api' },
  { sensor: 'claude', method: 'otel' },
  { sensor: 'codex', method: 'analytics_api' },
  { sensor: 'codex', method: 'platform_admin_api' },
  { sensor: 'codex', method: 'otel' },
  { sensor: 'windsurf', method: 'analytics_api' },
  { sensor: 'copilot', method: 'classic' },
  { sensor: 'copilot', method: 'fine_grained' },
  { sensor: 'copilot', method: 'enterprise' }
]

// Derived from ICONS_CONFIG so labels stay in sync with SensorsIcon display, plus
// AI method sub-options. Sorted by [name, badge] so each method nests under its
// parent tool; `badge` (method) renders as a chip next to `name`.
const INTEGRATION_OPTIONS = sortBy(
  [
    ...(Object.entries(ICONS_CONFIG) as [SensorName, { label: string }][]).map(
      ([value, { label }]) => ({
        value,
        name: label,
        icon: value,
        badge: '',
        disabled: false
      })
    ),
    ...AI_METHOD_FILTERS.map(({ sensor, method, disabled = false }) => ({
      value: `${sensor}:${method}`,
      name: ICONS_CONFIG[sensor].label,
      icon: sensor,
      disabled,
      badge:
        sensor === 'claude'
          ? CLAUDE_METHOD_LABEL[method]
          : sensor === 'codex'
            ? CODEX_METHOD_LABEL[method]
            : sensor === 'windsurf'
              ? WINDSURF_METHOD_LABEL[method]
              : COPILOT_AUTH_TYPE_LABEL[method]
    }))
  ],
  ['name', 'badge']
)

const toFilterParam = (value: string | undefined | null): string | null =>
  value && value !== ALL_FILTER ? value : null

function deriveFilterParams(filters) {
  const [sensorName = null, sensorMethod = null] =
    toFilterParam(filters.sensorToggle)?.split(':') ?? []
  const deployment = toFilterParam(filters.deploymentToggle)
  const isAll = filters.toggle === toggleStates.ALL
  return {
    isPaid: isAll || filters.toggle === toggleStates.PAID,
    isTrial: isAll || filters.toggle === toggleStates.TRIAL,
    isActive:
      filters.activeToggle === activeToggleStates.ACTIVE ? 'true' : 'false',
    planName: toFilterParam(filters.planToggle),
    sensorName,
    sensorMethod,
    isOpa: deployment ? deployment === 'opa' : null
  }
}

const Organizations = () => {
  const [loadingSupport, setLoadingSupport] = useState(null)
  const [isExporting, setIsExporting] = useState(false)
  const [editingSettingDefinition, setEditingSettingDefinition] = useState(null)
  const [editingOrgId, setEditingOrgId] = useState(null)
  const [editingOrgName, setEditingOrgName] = useState(null)
  const [selectedFlag, setSelectedFlag] = useState(null)
  // FeatureFlagFilter keeps its own input state, so a reset remounts it
  const [flagFilterKey, setFlagFilterKey] = useState(0)
  const [insightsTarget, setInsightsTarget] = useState<{
    id: string | number
    name: string
  } | null>(null)
  const navigate = useNavigate()
  const setSupportAccount = useSetSupportAccount()
  const queryClient = useQueryClient()
  const userInfo = useUserInfo()
  const email = userInfo?.email || null

  // Read once, not per render: useFilteredTableQuery writes ?toggle= only when the
  // toggle differs from defaultToggle, so a re-reading default would drop it from the URL.
  const [defaultTier] = useState(() => {
    const stored = orgTierPreferenceStorage.get(email)
    return Object.values(toggleStates).includes(stored) ? stored : toggleStates.PAID
  })
  const { use24Hour } = useTimeFormat()
  const isAdmin = userInfo?.scopes?.includes('admin') ?? false
  // Flipping a flag for an org depends on the flag's lifecycle status, not on
  // edit_org_ff — mirrors the rule the backend enforces on the write.
  const selectedFlagBlockedReason = selectedFlag
    ? getOrgToggleBlockedReason(selectedFlag.status, userInfo?.scopes)
    : null
  const { data: multiOrgData } = useMultiOrgGroups()
  const {
    favoriteIds,
    showOnly: showFavoritesOnly,
    setShowOnly: setShowFavoritesOnly
  } = useFavoriteOrgs()
  // Opt-in: also match user name/email in search (slow — off by default)
  const [searchUsers, setSearchUsers] = useState<boolean>(
    () => localStorage.getItem('orgSearchUsers') === 'true'
  )
  const { prefs: insightsPrefs } = useInsightsPreferences()
  // Health scores come from BigQuery
  const showHealth = HAS_US_DATA_SOURCES && insightsPrefs.showHealth

  useEffect(() => {
    if (!insightsPrefs.enabled) {
      setInsightsTarget(null)
    }
  }, [insightsPrefs.enabled])

  // Map org ID → multi-org group for quick lookup
  const orgToMultiOrgMap = useMemo(() => {
    const map = {}
    ;(multiOrgData?.items || []).forEach(group => {
      ;(group.organization_ids || []).forEach(orgId => {
        map[orgId] = {
          id: group.id,
          name: group.name,
          orgCount: group.organization_ids?.length || 0
        }
      })
    })
    return map
  }, [multiOrgData])

  const {
    data: organizations,
    isLoading,
    isFetching,
    isError,
    totalCount,
    page,
    rowsPerPage,
    toggleMenu,
    inputValue,
    handleSearchChange,
    handleToggleChange,
    handlePageChange,
    handleRowsPerPageChange,
    additionalToggleValues,
    handleAdditionalToggleChange,
    featureFlagFilter,
    handleFeatureFlagChange,
    featureFlagInverted,
    handleFeatureFlagInvertedChange,
    handleResetFilters,
    sortConfig,
    handleSort
  } = useFilteredTableQuery({
    queryKeyFn: filters => {
      if (showFavoritesOnly) {
        // Separate key namespace: favorite IDs drive the fetch; tier/active/
        // plan/sensor toggles are not sent to the server in this mode.
        return queryKeys.organizations.list({
          mode: 'favorites',
          orgIds: [...favoriteIds].map(String).sort(),
          page: filters.page,
          limit: filters.limit,
          searchParam: filters.searchParam,
          searchUsers,
          sortKey: filters.sortKey,
          sortDirection: filters.sortDirection
        })
      }
      return queryKeys.organizations.list({
        page: filters.page,
        limit: filters.limit,
        searchParam: filters.searchParam,
        featureFlagParam: filters.featureFlagParam,
        featureFlagInverted: filters.featureFlagInverted,
        ...deriveFilterParams(filters),
        searchUsers,
        sortKey: filters.sortKey,
        sortDirection: filters.sortDirection
      })
    },
    queryFn: async (filters, signal) => {
      if (showFavoritesOnly) {
        if (favoriteIds.length === 0) {
          return { items: [], total_count: 0 } as PaginatedData<Organization>
        }
        const { data } = await getOrganizationsByIds(
          favoriteIds,
          {
            page: filters.page,
            limit: filters.limit,
            searchParam: filters.searchParam,
            searchUsers,
            sortKey: filters.sortKey,
            sortDirection: filters.sortDirection
          },
          signal
        )
        return data as PaginatedData<Organization>
      }
      const { data } = await getOrganizations({
        page: filters.page,
        limit: filters.limit,
        searchParam: filters.searchParam,
        featureFlagParam: filters.featureFlagParam,
        featureFlagInverted: filters.featureFlagInverted,
        ...deriveFilterParams(filters),
        searchUsers,
        sortKey: filters.sortKey,
        sortDirection: filters.sortDirection,
        signal
      })
      return data as PaginatedData<Organization>
    },
    defaultToggle: defaultTier,
    additionalToggles: {
      activeToggle: activeToggleStates.ACTIVE,
      planToggle: ALL_FILTER,
      deploymentToggle: ALL_FILTER,
      sensorToggle: ALL_FILTER
    },
    syncWithUrl: true
  })

  // Org details is the usual next click and its chunk pulls in a large dep graph,
  // so warm it once the list is in — otherwise the first navigation waits on the
  // chunk before it can even start fetching the org's data. Waiting on isLoading
  // keeps those preloads from competing with the list request itself.
  useEffect(() => {
    if (isLoading) {
      return
    }
    const warmOrgDetails = async () => {
      try {
        await import('../OrganizationDetails')
      } catch {
        // Best-effort only; a real navigation reports the load failure itself.
      }
    }
    warmOrgDetails()
  }, [isLoading])

  const isTrialView = toggleMenu === toggleStates.TRIAL

  const trackFilter = useCallback(
    (filter: string, value: unknown) =>
      trackAction('org_filter_changed', { filter, value }),
    []
  )

  const handleFavoritesOnlyChange = useCallback(
    (value: boolean) => {
      trackFilter('favorites', value)
      setShowFavoritesOnly(value)
      handlePageChange(0)
    },
    [trackFilter, setShowFavoritesOnly, handlePageChange]
  )

  // Tier resets to the app default (Paid), not the stored preference, which only
  // mirrors the last tier picked and would make the reset a no-op for it
  const hasActiveFilters =
    inputValue.trim() !== '' ||
    toggleMenu !== toggleStates.PAID ||
    additionalToggleValues.activeToggle !== activeToggleStates.ACTIVE ||
    (additionalToggleValues.planToggle ?? ALL_FILTER) !== ALL_FILTER ||
    (additionalToggleValues.deploymentToggle ?? ALL_FILTER) !== ALL_FILTER ||
    (additionalToggleValues.sensorToggle ?? ALL_FILTER) !== ALL_FILTER ||
    featureFlagFilter !== null ||
    featureFlagInverted ||
    showFavoritesOnly

  const handleResetAllFilters = useCallback(() => {
    trackAction('org_filters_reset', {})
    orgTierPreferenceStorage.set(toggleStates.PAID, email)
    handleResetFilters(toggleStates.PAID)
    setShowFavoritesOnly(false)
    setSelectedFlag(null)
    setFlagFilterKey(key => key + 1)
  }, [email, handleResetFilters, setShowFavoritesOnly])

  const handleSearchUsersChange = useCallback(
    (value: boolean) => {
      trackFilter('search_users', value)
      setSearchUsers(value)
      localStorage.setItem('orgSearchUsers', value ? 'true' : 'false')
      handlePageChange(0)
    },
    [trackFilter, handlePageChange]
  )

  const handleSetSupportAccount = useCallback(
    async (e, orgId, orgName) => {
      e.stopPropagation()
      setLoadingSupport(orgId)
      try {
        await setSupportAccount.mutateAsync(orgId)
        trackAction('support_account_set', { orgId, orgName })
        showSupportAccountSuccessToast()
      } catch (exp) {
        showSupportAccountErrorToast()
        console.error(exp, exp.stack)
      } finally {
        setLoadingSupport(null)
      }
    },
    [setSupportAccount]
  )

  const fetchExportData = useCallback(async () => {
    if (showFavoritesOnly) {
      if (favoriteIds.length === 0) {
        return []
      }
      const { data } = await getOrganizationsByIds(favoriteIds, {
        page: 1,
        limit: 10000,
        searchParam: inputValue || null,
        searchUsers,
        sortKey: sortConfig.key,
        sortDirection: sortConfig.direction
      })
      return data?.items || []
    }
    const { data } = await getOrganizations({
      page: 1,
      limit: 10000,
      searchParam: inputValue || null,
      featureFlagParam: featureFlagFilter,
      featureFlagInverted,
      ...deriveFilterParams({ toggle: toggleMenu, ...additionalToggleValues }),
      searchUsers,
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction
    })
    return data?.items || []
  }, [
    showFavoritesOnly,
    favoriteIds,
    toggleMenu,
    additionalToggleValues,
    inputValue,
    featureFlagFilter,
    featureFlagInverted,
    searchUsers,
    sortConfig
  ])

  const getExportColumns = useCallback(() => {
    const columns = [
      { header: '#', accessor: (_row, index) => index + 1 },
      { header: 'Organization Name', accessor: 'name' },
      ...(HAS_US_DATA_SOURCES
        ? [{ header: 'SF Account Name', accessor: 'sfdc_account_name' }]
        : []),
      { header: 'Organization ID', accessor: 'id' },
      { header: 'License Type', accessor: 'license_tier_type' },
      { header: 'License Plan', accessor: 'license_plan_name' },
      ...(showFavoritesOnly
        ? [
            {
              header: 'Active',
              accessor: row => (row.is_active ? 'Yes' : 'No')
            }
          ]
        : []),
      ...(isTrialView
        ? [
            {
              header: 'Trial End',
              accessor: row => row.trial_end_date || ''
            }
          ]
        : []),
      { header: 'Billable Users', accessor: 'billable_users' },
      ...(HAS_US_DATA_SOURCES
        ? [{ header: 'ARR', accessor: row => row.ARR || row.parent_ARR || '' }]
        : []),
      {
        header: 'Latest Login',
        accessor: row =>
          row.latest_login
            ? formatDateTime(parseUtc(row.latest_login), use24Hour, 'YYYY-MM-DD')
            : 'Never'
      },
      {
        header: 'Integrations',
        accessor: row =>
          Array.isArray(row.sensors) ? row.sensors.join(' | ') : ''
      }
    ]
    if (featureFlagFilter) {
      columns.push({
        header: `Feature Flag: ${featureFlagFilter}`,
        accessor: () => (!featureFlagInverted ? 'true' : 'false')
      })
    }
    return columns
  }, [
    featureFlagFilter,
    featureFlagInverted,
    isTrialView,
    showFavoritesOnly,
    use24Hour
  ])

  const getExportFilename = useCallback(
    extension => {
      const timestamp = getFilenameTimestamp()
      if (featureFlagFilter) {
        const state = featureFlagInverted ? 'disabled' : 'enabled'
        return `${featureFlagFilter}_${state}_${timestamp}.${extension}`
      }
      return `organizations_${timestamp}.${extension}`
    },
    [featureFlagFilter, featureFlagInverted]
  )

  const handleExport = useCallback(
    async action => {
      setIsExporting(true)
      try {
        const items = await fetchExportData()
        const columns = getExportColumns()

        trackAction('data_exported', {
          action,
          rowCount: items.length,
          featureFlagFilter,
          featureFlagInverted
        })

        switch (action) {
          case 'download-csv': {
            const csv = convertToCsv(items, columns)
            downloadFile(csv, getExportFilename('csv'))
            lbToast({
              title: 'Export complete',
              description: `Downloaded ${items.length} organizations as CSV`,
              variant: 'success'
            })
            break
          }
          case 'copy-csv': {
            const csv = convertToCsv(items, columns)
            await navigator.clipboard.writeText(csv)
            lbToast({
              title: 'Copied to clipboard',
              description: `${items.length} organizations copied as CSV`,
              variant: 'success'
            })
            break
          }
          case 'download-json': {
            const jsonArray = convertToJsonArray(items, columns)
            const jsonStr = JSON.stringify(jsonArray, null, 2)
            downloadFile(
              jsonStr,
              getExportFilename('json'),
              'application/json;charset=utf-8;'
            )
            lbToast({
              title: 'Export complete',
              description: `Downloaded ${items.length} organizations as JSON`,
              variant: 'success'
            })
            break
          }
          case 'copy-json': {
            const jsonArray = convertToJsonArray(items, columns)
            const jsonStr = JSON.stringify(jsonArray, null, 2)
            await navigator.clipboard.writeText(jsonStr)
            lbToast({
              title: 'Copied to clipboard',
              description: `${items.length} organizations copied as JSON`,
              variant: 'success'
            })
            break
          }
        }
      } catch (err) {
        console.error('Export failed:', err)
        lbToast({
          title: 'Export failed',
          description: 'Failed to export organizations',
          variant: 'destructive'
        })
      } finally {
        setIsExporting(false)
      }
    },
    [
      fetchExportData,
      getExportColumns,
      getExportFilename,
      featureFlagFilter,
      featureFlagInverted
    ]
  )

  const tableColumns = useMemo(() => {
    // The Active column is redundant when the ACTIVE/INACTIVE toggle is the
    // source of truth (every visible row matches the toggle). Favorites mode
    // bypasses that toggle, so the column is only meaningful there.
    let base = showFavoritesOnly
      ? ORGANIZATION_COLUMNS
      : ORGANIZATION_COLUMNS.filter(c => c.sortKey !== 'is_active')
    const hiddenColumns = new Set<string>()
    if (!insightsPrefs.enabled) {
      hiddenColumns.add('Insights')
    }
    if (!insightsPrefs.showAiChats) {
      hiddenColumns.add('AI Chats')
    }
    if (!insightsPrefs.showDashboards) {
      hiddenColumns.add('Dashboards')
    }
    // ARR comes from Salesforce
    if (!HAS_US_DATA_SOURCES) {
      hiddenColumns.add('ARR')
    }
    if (!showHealth) {
      hiddenColumns.add('Health')
    }
    if (hiddenColumns.size > 0) {
      base = base.filter(c => !hiddenColumns.has(c.title))
    }
    if (isTrialView) {
      const insertAt =
        base.findIndex(c => c.sortKey === 'license_plan_name') + 1
      base = [
        ...base.slice(0, insertAt),
        TRIAL_END_COLUMN,
        ...base.slice(insertAt)
      ]
    }
    if (!featureFlagFilter) {
      return base
    }
    // Row cells render the feature-flag cell between Integrations and the
    // Actions/Support cell, so insert the column header at the same spot to
    // keep header order aligned with the row cells.
    const actionsIdx = base.findIndex(c => c.title === 'Actions')
    return [
      ...base.slice(0, actionsIdx),
      {
        title: featureFlagInverted
          ? 'Feature Flag (false)'
          : 'Feature Flag (true)',
        width: '8%',
        sortKey: 'feature_flag_value'
      },
      ...base.slice(actionsIdx)
    ]
  }, [
    featureFlagFilter,
    featureFlagInverted,
    isTrialView,
    showFavoritesOnly,
    insightsPrefs.enabled,
    insightsPrefs.showAiChats,
    insightsPrefs.showDashboards,
    showHealth
  ])

  const handleFlagSelect = useCallback(flag => {
    setSelectedFlag(flag ?? null)
  }, [])

  const clearEditingState = useCallback(() => {
    setEditingSettingDefinition(null)
    setEditingOrgId(null)
    setEditingOrgName(null)
  }, [])

  const onSettingsUpdated = useCallback(() => {
    // Not the whole ['organizations'] tree: that refetches every row's badges at once
    queryClient.invalidateQueries({ queryKey: queryKeys.organizations.lists() })
    clearEditingState()
  }, [queryClient, clearEditingState])

  const tableRows = useMemo(() => {
    const orgList = organizations as Organization[]
    return orgList.map((org: Organization, index: number) => {
      const {
        id,
        sensors,
        sensor_methods: sensorMethods,
        sensor_custom_domains: sensorCustomDomains,
        name,
        license_tier_type: licenseTierType,
        license_plan_name: licensePlanName,
        is_active: isActive,
        billable_users: billableUsers,
        ARR,
        parent_ARR,
        latest_login: latestLogin,
        trial_end_date: trialEndDate,
        health
      } = org
      const effectiveArr = ARR || parent_ARR
      const isStale =
        latestLogin &&
        parseUtc(latestLogin).isBefore(dayjs().subtract(3, 'month'))
      const isTrialExpired =
        trialEndDate && parseUtc(trialEndDate).isBefore(dayjs(), 'day')
      return (
        <LBTableRow
          key={id || index}
          className='cursor-pointer hover:bg-accent'
          onClick={() => navigate(`/organization/${id}`)}
        >
          <LBTableCell>
            <FavoriteOrgButton orgId={id} size='sm' stopPropagation />
          </LBTableCell>
          <LBTableCell>
            <div className='flex items-center gap-2'>
              <CompanyLogo
                website={org.sfdc_website}
                name={name}
                variant='md'
              />
              <div className='flex flex-col'>
                <span className='font-bold' title={name}>
                  {name}
                </span>
                {org.sfdc_account_name !== name && (
                  <SfdcAccountName name={org.sfdc_account_name} />
                )}
              </div>
              {orgToMultiOrgMap[id] && (
                <LBTooltip>
                  <LBTooltipTrigger asChild>
                    <span
                      onClick={e => {
                        e.stopPropagation()
                        navigate(`/multi-org/${orgToMultiOrgMap[id].id}`)
                      }}
                    >
                      <LBBadge
                        variant='default'
                        className='text-[10px] px-2 py-0.5 cursor-pointer bg-purple-600 hover:bg-purple-700'
                      >
                        {orgToMultiOrgMap[id].name}
                      </LBBadge>
                    </span>
                  </LBTooltipTrigger>
                  <LBTooltipContent className='px-3 py-1.5'>
                    {orgToMultiOrgMap[id].orgCount} orgs
                  </LBTooltipContent>
                </LBTooltip>
              )}
            </div>
          </LBTableCell>
          <LBTableCell>
            <div
              className='flex items-center gap-1'
              onClick={e => e.stopPropagation()}
            >
              <CopyButton text={id} description={`Organization ID: ${id}`} />
              <span className='font-mono text-sm'>{id}</span>
            </div>
          </LBTableCell>
          <LBTableCell>
            <LicenseTypeBadge value={licenseTierType} />
          </LBTableCell>
          <LBTableCell>
            <LicensePlanBadge value={licensePlanName} />
          </LBTableCell>
          {isTrialView && (
            <LBTableCell>
              {trialEndDate ? (
                <LBTooltip>
                  <LBTooltipTrigger asChild>
                    <span
                      className={
                        isTrialExpired ? 'text-destructive font-semibold' : ''
                      }
                    >
                      {parseUtc(trialEndDate).format('MMM D, YYYY')}
                    </span>
                  </LBTooltipTrigger>
                  <LBTooltipContent>
                    {`${isTrialExpired ? 'Expired' : 'Expires'} ${parseUtc(trialEndDate).fromNow()}`}
                  </LBTooltipContent>
                </LBTooltip>
              ) : (
                <span className='text-muted-foreground'>—</span>
              )}
            </LBTableCell>
          )}
          {showFavoritesOnly && (
            <LBTableCell>
              <div className={isActive ? 'switch-on' : ''}>
                <LBSwitch checked={isActive} disabled />
              </div>
            </LBTableCell>
          )}
          <LBTableCell>{billableUsers?.toLocaleString() ?? '—'}</LBTableCell>
          {HAS_US_DATA_SOURCES && (
            <LBTableCell>
              {!effectiveArr ? (
                <span className='text-muted-foreground'>—</span>
              ) : ARR ? (
                usd.format(effectiveArr)
              ) : (
                <LBTooltip>
                  <LBTooltipTrigger asChild>
                    <span className='underline decoration-dotted underline-offset-2'>
                      {usd.format(effectiveArr)}
                    </span>
                  </LBTooltipTrigger>
                  <LBTooltipContent>
                    Parent account ARR — this org has no ARR of its own
                  </LBTooltipContent>
                </LBTooltip>
              )}
            </LBTableCell>
          )}
          <LBTableCell>
            {latestLogin ? (
              <LBTooltip>
                <LBTooltipTrigger asChild>
                  <span
                    className={isStale ? 'text-destructive font-semibold' : ''}
                  >
                    {parseUtc(latestLogin).format('MMM D, YYYY')}
                  </span>
                </LBTooltipTrigger>
                <LBTooltipContent>
                  {parseUtc(latestLogin).fromNow()}
                </LBTooltipContent>
              </LBTooltip>
            ) : (
              <span className='text-muted-foreground'>Never</span>
            )}
          </LBTableCell>
          <LBTableCell>
            <div className='flex gap-1'>
              {sortBy(sensors || [], s => s?.toLowerCase()).map(
                (sensorName, idx) =>
                  sensorName ? (
                    <SensorsIcon
                      key={idx}
                      name={sensorName.toLowerCase() as SensorName}
                      methods={sensorMethods?.[sensorName.toLowerCase()]}
                      customDomains={
                        sensorCustomDomains?.[sensorName.toLowerCase()]
                      }
                    />
                  ) : null
              )}
            </div>
          </LBTableCell>
          {featureFlagFilter && (
            <LBTableCell>
              <div onClick={e => e.stopPropagation()}>
                {/* The filter hydrates the flag asynchronously from the ?ff= param.
                    Until it lands there is no status to judge and no definition_id
                    to send, so show the placeholder rather than a toggle nobody
                    (admins included) could use correctly. */}
                {selectedFlag ? (
                  <FeatureFlagOrgToggle
                    blockedReason={selectedFlagBlockedReason}
                    isAdmin={isAdmin}
                    checked={!featureFlagInverted}
                    onCheckedChange={() => {
                      setEditingOrgId(org.id)
                      setEditingOrgName(org.name)
                      setEditingSettingDefinition({
                        definition_id: selectedFlag.id,
                        name: featureFlagFilter,
                        value: featureFlagInverted,
                        from_value: !featureFlagInverted,
                        isFeatureFlags: true,
                        adminOverrideReason: selectedFlagBlockedReason
                      })
                    }}
                  />
                ) : (
                  <LBSkeleton className='h-5 w-9 rounded-full' />
                )}
              </div>
            </LBTableCell>
          )}
          <LBTableCell>
            <LBButton
              size='sm'
              variant='outline'
              onClick={e => handleSetSupportAccount(e, id, name)}
              disabled={loadingSupport === id}
            >
              {loadingSupport === id ? 'Setting...' : 'Support'}
            </LBButton>
          </LBTableCell>
          {insightsPrefs.enabled && (
            <LBTableCell>
              <InsightsBadge
                orgId={id}
                orgName={name}
                onOpen={setInsightsTarget}
              />
            </LBTableCell>
          )}
          {insightsPrefs.showAiChats && (
            <LBTableCell>
              <AiChatsBadge orgId={id} />
            </LBTableCell>
          )}
          {insightsPrefs.showDashboards && (
            <LBTableCell>
              <CustomDashboardsBadge orgId={id} />
            </LBTableCell>
          )}
          {showHealth && (
            <LBTableCell>
              {/* Comes with the list payload — no per-row request needed */}
              <HealthScoreBadge health={health} />
            </LBTableCell>
          )}
        </LBTableRow>
      )
    })
  }, [
    organizations,
    navigate,
    handleSetSupportAccount,
    loadingSupport,
    featureFlagFilter,
    featureFlagInverted,
    selectedFlag,
    selectedFlagBlockedReason,
    isAdmin,
    orgToMultiOrgMap,
    isTrialView,
    showFavoritesOnly,
    insightsPrefs.enabled,
    insightsPrefs.showAiChats,
    insightsPrefs.showDashboards,
    showHealth
  ])

  return (
    <div>
      {isError && (
        <LBAlert variant='destructive' className='mb-4 max-w-md'>
          <LBAlertDescription>Failed to load organizations</LBAlertDescription>
        </LBAlert>
      )}

      <OrgInsightsDrawer
        orgId={insightsTarget?.id}
        orgName={insightsTarget?.name}
        open={insightsTarget !== null}
        onOpenChange={next => {
          if (!next) {
            setInsightsTarget(null)
          }
        }}
        hideTrigger
      />

      <FilteredTable
        title='Organizations'
        toggleStatesList={toggleStates}
        tableRows={tableRows}
        tableColumns={tableColumns}
        tableId='OPATable'
        searchPlaceholder={
          searchUsers
            ? 'Organization name, ID, user name, or email'
            : 'Organization name or ID'
        }
        customAction={
          <LBDropdownMenu>
            <LBDropdownMenuTrigger asChild>
              <LBButton
                disabled={isExporting}
                aria-label={isExporting ? undefined : 'Export'}
              >
                <LBExportFileIcon className='h-4 w-4' />
                {isExporting && <span className='ml-1.5'>Exporting...</span>}
                <LBChevronDownIcon className='h-4 w-4 ml-1' />
              </LBButton>
            </LBDropdownMenuTrigger>
            <LBDropdownMenuContent align='end'>
              <LBDropdownMenuItem onClick={() => handleExport('download-csv')}>
                <LBExportFileIcon className='h-4 w-4 mr-2' />
                Download CSV
              </LBDropdownMenuItem>
              <LBDropdownMenuItem onClick={() => handleExport('copy-csv')}>
                <LBCopyIcon className='h-4 w-4 mr-2' />
                Copy CSV
              </LBDropdownMenuItem>
              <LBDropdownMenuSeparator />
              <LBDropdownMenuItem onClick={() => handleExport('download-json')}>
                <LBExportFileIcon className='h-4 w-4 mr-2' />
                Download JSON
              </LBDropdownMenuItem>
              <LBDropdownMenuItem onClick={() => handleExport('copy-json')}>
                <LBCopyIcon className='h-4 w-4 mr-2' />
                Copy JSON
              </LBDropdownMenuItem>
            </LBDropdownMenuContent>
          </LBDropdownMenu>
        }
        afterSearch={
          <LBTooltip>
            <LBTooltipTrigger asChild>
              <span className='inline-flex'>
                <LBSwitch
                  id='search-users-toggle'
                  aria-label='Also search by user name and email'
                  checked={searchUsers}
                  onCheckedChange={handleSearchUsersChange}
                />
              </span>
            </LBTooltipTrigger>
            <LBTooltipContent>Also match user name and email</LBTooltipContent>
          </LBTooltip>
        }
        afterToggles={
          <div className='flex items-center gap-2 whitespace-nowrap'>
            <LBSwitch
              id='favorites-only-toggle'
              checked={showFavoritesOnly}
              onCheckedChange={handleFavoritesOnlyChange}
            />
            <label
              htmlFor='favorites-only-toggle'
              className='text-sm cursor-pointer select-none inline-flex items-center gap-1'
            >
              <LBStarIcon
                className='h-3.5 w-3.5 text-yellow-500 shrink-0'
                fill='currentColor'
              />
              Favorites only
            </label>
          </div>
        }
        onSort={handleSort}
        sortConfig={sortConfig}
        isLoading={isLoading}
        isFetching={isFetching}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        toggleMenu={toggleMenu}
        inputValue={inputValue}
        onSearchChange={handleSearchChange}
        onToggleChange={value => {
          trackFilter('status', value)
          orgTierPreferenceStorage.set(value, email)
          handleToggleChange(value)
        }}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        secondToggleStatesList={activeToggleStates}
        secondToggleMenu={additionalToggleValues.activeToggle}
        onSecondToggleChange={value => {
          trackFilter('active', value)
          handleAdditionalToggleChange('activeToggle', value)
        }}
        customFilter={
          <div className='flex flex-wrap items-start gap-3'>
            <LBSelect
              value={additionalToggleValues.planToggle || ALL_FILTER}
              onValueChange={value => {
                trackFilter('plan', value)
                handleAdditionalToggleChange('planToggle', value)
              }}
            >
              <LBSelectTrigger className='w-40'>
                <LBSelectValue placeholder='All Plans' />
              </LBSelectTrigger>
              <LBSelectContent>
                <LBSelectItem value={ALL_FILTER}>All Plans</LBSelectItem>
                {PLAN_OPTIONS.map(({ value, label }) => (
                  <LBSelectItem key={value} value={value}>
                    {label}
                  </LBSelectItem>
                ))}
              </LBSelectContent>
            </LBSelect>
            <LBSelect
              value={additionalToggleValues.deploymentToggle || ALL_FILTER}
              onValueChange={value => {
                trackFilter('deployment', value)
                handleAdditionalToggleChange('deploymentToggle', value)
              }}
            >
              <LBSelectTrigger className='w-44'>
                <LBSelectValue placeholder='All Deployments' />
              </LBSelectTrigger>
              <LBSelectContent>
                <LBSelectItem value={ALL_FILTER}>All Deployments</LBSelectItem>
                {DEPLOYMENT_OPTIONS.map(({ value, label }) => (
                  <LBSelectItem key={value} value={value}>
                    {label}
                  </LBSelectItem>
                ))}
              </LBSelectContent>
            </LBSelect>
            <LBSelect
              value={additionalToggleValues.sensorToggle || ALL_FILTER}
              onValueChange={value => {
                trackFilter('integration', value)
                handleAdditionalToggleChange('sensorToggle', value)
              }}
            >
              <LBSelectTrigger className='w-48'>
                <LBSelectValue placeholder='All Integrations' />
              </LBSelectTrigger>
              <LBSelectContent>
                <LBSelectItem value={ALL_FILTER}>All Integrations</LBSelectItem>
                {INTEGRATION_OPTIONS.map(
                  ({ value, name, icon, badge, disabled }) => (
                    <LBSelectItem key={value} value={value} disabled={disabled}>
                      <span className='inline-flex items-center gap-2'>
                        <SensorsIcon name={icon} size={16} />
                        {name}
                        {badge && (
                          <LBBadge
                            variant='secondary'
                            className='text-xs px-2 py-0.5 shrink-0'
                          >
                            {badge}
                          </LBBadge>
                        )}
                      </span>
                    </LBSelectItem>
                  )
                )}
              </LBSelectContent>
            </LBSelect>
            {/* Flag filter and its permission indicator stack together so the
                scope note sits under the flag it refers to, not far left */}
            <div className='flex flex-col gap-1'>
              <FeatureFlagFilter
                key={flagFilterKey}
                onChange={value => {
                  trackFilter('featureFlag', value)
                  handleFeatureFlagChange(value)
                }}
                onFlagSelect={handleFlagSelect}
                inverted={featureFlagInverted}
                onInvertedChange={handleFeatureFlagInvertedChange}
                initialValue={featureFlagFilter}
              />
              {featureFlagFilter && <FeatureFlagRoleIndicator />}
            </div>
            {/* Always rendered (disabled when idle) so the filter row doesn't shift */}
            <LBButton
              variant='outline'
              disabled={!hasActiveFilters}
              onClick={handleResetAllFilters}
            >
              <LBFilterOffIcon className='h-4 w-4 mr-1.5' />
              Reset filters
            </LBButton>
          </div>
        }
      />

      <SettingChangeConfirmationDialog
        organizationId={editingOrgId}
        organizationName={editingOrgName}
        settingDefinition={editingSettingDefinition}
        open={Boolean(editingSettingDefinition)}
        onSettingsUpdated={onSettingsUpdated}
        onClose={clearEditingState}
      />
    </div>
  )
}

export { Organizations }
